import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useCfaDemoEnabled } from 'utils/featureFlags';
import {
  DEFAULT_ACTIVE_HURRICANES_SOURCE,
  DEFAULT_TROPICAL_CONDITIONS,
  useCreateOrgEventAutomationRule,
  useOrgEventAutomationRules,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { NotificationType } from 'hooks/useNotification';

export const CFA_DEFAULT_TROPICAL_RULE_NAME = 'CFA Tropical Storm Impact';

/** Shared locks so React Strict Mode remounts do not double-create. */
const ensureLocks = new Map<string, Promise<unknown>>();

/**
 * Ensures the CFA group has a default tropical automation rule (disabled by
 * default so the hardcoded Memphis demo stays primary). Users can enable it
 * under Event Settings to exercise the prod path.
 */
export function useEnsureCfaTropicalAutomationRule() {
  const cfaEnabled = useCfaDemoEnabled();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const { data: rules, isSuccess } = useOrgEventAutomationRules({
    enabled: cfaEnabled && !!groupGuid,
  });
  const createRule = useCreateOrgEventAutomationRule();
  const attempted = useRef(false);

  useEffect(() => {
    if (!cfaEnabled || !groupGuid || !isSuccess || attempted.current) return;
    if ((rules?.length ?? 0) > 0) {
      attempted.current = true;
      return;
    }

    const lockKey = `cfa-tropical-rule:${groupGuid}`;
    if (ensureLocks.has(lockKey)) return;

    attempted.current = true;
    const promise = createRule
      .mutateAsync({
        group_guid: groupGuid,
        name: CFA_DEFAULT_TROPICAL_RULE_NAME,
        enabled: false,
        logic: 'all',
        source: DEFAULT_ACTIVE_HURRICANES_SOURCE,
        conditions: DEFAULT_TROPICAL_CONDITIONS,
        event_template: {
          severity: 'High',
          type: 'Tropical Storm',
          region: 'CFA Southeast',
          thresholdDescription:
            'Storm category ≥ Tropical Storm (TS) and ≥ 1 CFA resource in forecast cone',
        },
        notify_on_create: false,
        checklist_on_create: false,
        checklist_config: { items: [] },
        notify_config: {
          notificationTypes: [NotificationType.IN_APP],
          userIds: userGuid ? [userGuid] : [],
          teamIds: [],
          priority: 'watch',
          titleTemplate: 'Threshold event: {eventName}',
          bodyTemplate:
            '{eventName}: {stormName} impacts {resourceCount} resources.',
        },
        created_by_user_guid: userGuid ?? null,
      })
      .catch((err) => {
        console.warn(
          '[cfa] failed to ensure default tropical automation rule',
          err
        );
        attempted.current = false;
      })
      .finally(() => {
        ensureLocks.delete(lockKey);
      });

    ensureLocks.set(lockKey, promise);
  }, [cfaEnabled, groupGuid, userGuid, isSuccess, rules, createRule]);
}
